import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, List, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { ExportButtons } from "@/components/common/ExportButtons";
import { DataTable } from "@/components/common/DataTable";
import { PriorityBadge, SlaBadge, StatusBadge } from "@/components/common/StatusBadge";
import { KanbanBoard } from "@/components/tickets/KanbanBoard";
import { NewRequestDialog } from "@/components/tickets/NewRequestDialog";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_META, TICKET_STATUS, fmtDateTime, fmtRemaining } from "@/lib/constants";

export default function Tickets() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [view, setView] = useState("kanban");
  const [svc, setSvc] = useState("all");
  const [st, setSt] = useState("active");
  const [open, setOpen] = useState(false);
  const [reset, setReset] = useState(false);
  const [confirm, setConfirm] = useState("");
  const q = new URLSearchParams();
  if (svc !== "all") q.set("service_type", svc);
  if (st === "active") q.set("status", "pending_approval,revise,open,in_progress,on_hold,resolved");
  else if (st !== "all") q.set("status", st);
  const { data, loading, refetch } = useFetch(`/tickets?${q.toString()}`);
  const rows = data || [];
  const doReset = async () => {
    try {
      const { data: r } = await api.post("/tickets/reset-all", { confirm });
      toast.success(`Reset selesai: ${r.deleted_tickets} ticket & ${r.deleted_work_orders} work order turunan dihapus`);
      setReset(false); setConfirm(""); refetch();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "code", label: "Kode", render: (t) => <span className="font-mono text-xs">{t.code}</span> },
    { key: "title", label: "Judul", render: (t) => <span className="font-medium">{t.title}</span> },
    { key: "service_type", label: "Layanan", render: (t) => SERVICE_META[t.service_type]?.title },
    { key: "status", label: "Status", render: (t) => <StatusBadge status={t.status} map={TICKET_STATUS} /> },
    { key: "priority", label: "Prioritas", render: (t) => <PriorityBadge priority={t.priority} /> },
    { key: "needed_at", label: "Dibutuhkan", render: (t) => <span className="font-mono text-xs">{fmtDateTime(t.needed_at)}</span> },
    { key: "sla_state", label: "SLA", render: (t) => <div className="flex items-center gap-1"><SlaBadge state={t.sla_state} />{t.sla_remaining_seconds != null && <span className="font-mono text-xs">{fmtRemaining(t.sla_remaining_seconds)}</span>}</div> },
    { key: "requester_name", label: "Requester" },
    { key: "assignee_name", label: "Assignee" },
    { key: "created_at", label: "Dibuat", render: (t) => <span className="text-xs">{fmtDateTime(t.created_at)}</span> },
  ];

  return (
    <div className="space-y-4 fade-up">
      <PageHeader title="Pipeline Service" subtitle="Kanban + tabel hybrid untuk seluruh request operasional — alur dari pengajuan, approval, pengerjaan hingga selesai.">
        <Select value={svc} onValueChange={setSvc}><SelectTrigger className="h-8 w-44" data-testid="filter-service"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua layanan</SelectItem>{Object.entries(SERVICE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.title}</SelectItem>)}</SelectContent></Select>
        <Select value={st} onValueChange={setSt}><SelectTrigger className="h-8 w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="active">Aktif</SelectItem><SelectItem value="all">Semua status</SelectItem>{Object.entries(TICKET_STATUS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent></Select>
        <div className="flex rounded-md border">
          <Button variant={view === "kanban" ? "secondary" : "ghost"} size="sm" onClick={() => setView("kanban")} data-testid="view-kanban-btn"><LayoutGrid className="h-4 w-4" /></Button>
          <Button variant={view === "table" ? "secondary" : "ghost"} size="sm" onClick={() => setView("table")} data-testid="view-table-btn"><List className="h-4 w-4" /></Button>
        </div>
        <ExportButtons collection="tickets" />
        {can() && <Button size="sm" variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => setReset(true)} data-testid="reset-tickets-btn"><RotateCcw className="h-4 w-4 mr-1" />Reset</Button>}
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-1" />Request</Button>
      </PageHeader>
      <Dialog open={reset} onOpenChange={(o) => { setReset(o); if (!o) setConfirm(""); }}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Reset semua Pipeline Service / Request?</DialogTitle>
            <DialogDescription>Semua ticket, komentar, activity log, dan work order yang dibuat dari ticket akan dihapus permanen. Nomor ticket kembali ke GA-{new Date().getFullYear()}-0001.<br /><b>Tidak dihapus:</b> master data (lokasi, departemen, kategori, vendor, tipe dokumen, user), aset, kendaraan, fasilitas, work order preventive mandiri, dan dokumen legal. Aksi ini tercatat di audit trail.</DialogDescription>
          </DialogHeader>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Ketik RESET untuk konfirmasi' data-testid="reset-confirm-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReset(false)} data-testid="reset-cancel-btn">Batal</Button>
            <Button variant="destructive" disabled={confirm !== "RESET"} onClick={doReset} data-testid="reset-confirm-btn">Hapus Semua Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {view === "kanban" ? <KanbanBoard tickets={rows} /> : (
        <DataTable columns={columns} rows={rows} loading={loading} searchKeys={["code", "title", "requester_name"]} testId="tickets-table"
          actions={(t) => <Button variant="outline" size="sm" onClick={() => nav(`/tickets/${t.id}`)} data-testid={`open-ticket-${t.code}`}>Detail</Button>} />
      )}
      {can("employee", "ga_staff") && null}
      <NewRequestDialog open={open} onOpenChange={setOpen} onCreated={(t) => nav(`/tickets/${t.id}`)} />
    </div>
  );
}
